import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { uploadFile, deleteFile } from '../services/adminApi';

const FileUpload = ({ value, onChange, type = 'images', accept = 'image/*', label = 'Upload File' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);

    setError('');
    setUploading(true);

    try {
      const result = await uploadFile(file, type);
      console.log('Upload result:', result);
      
      // Return full URL with backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${backendUrl}${result.url}`;
      console.log('Full URL:', fullUrl);
      
      onChange(fullUrl);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Upload failed';
      setError(errorMsg);
      console.error('Upload error:', err);
      console.error('Error details:', err.response?.data);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!value) return;
    
    try {
      // Extract the path from full URL
      const url = new URL(value);
      await deleteFile(url.pathname);
      onChange('');
    } catch (err) {
      console.error('Delete error:', err);
      // Even if delete fails, clear the value
      onChange('');
    }
  };

  const isVideo = type === 'videos' || accept.includes('video');

  return (
    <div className="space-y-3">
      <label className="text-white font-medium block">{label}</label>
      
      {value ? (
        <div className="relative">
          {/* Preview */}
          {isVideo ? (
            <video src={value} className="w-full h-48 object-cover rounded-lg" controls />
          ) : (
            <img src={value} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
          )}
          
          {/* Delete button */}
          <button
            type="button"
            onClick={handleDelete}
            className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 rounded-lg text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      ) : (
        <div>
          <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mb-3"></div>
                  <p className="text-sm text-gray-400">Uploading...</p>
                </>
              ) : (
                <>
                  {isVideo ? (
                    <VideoIcon className="w-12 h-12 mb-3 text-gray-400" />
                  ) : (
                    <ImageIcon className="w-12 h-12 mb-3 text-gray-400" />
                  )}
                  <p className="mb-2 text-sm text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    {isVideo ? 'MP4, MOV, AVI (Max 50MB)' : 'PNG, JPG, GIF, WEBP (Max 50MB)'}
                  </p>
                </>
              )}
            </div>
            <input
              type="file"
              className="hidden"
              accept={accept}
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}

      {/* Option to use URL instead */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter file URL:');
            if (url) onChange(url);
          }}
          className="text-sm text-blue-400 hover:text-blue-300 underline"
        >
          Or enter URL manually
        </button>
      </div>
    </div>
  );
};

export default FileUpload;
